import dayjs from 'dayjs';
import { Cancel, Edit, Save, CalendarToday } from '@mui/icons-material';
import CloseIcon from '@mui/icons-material/Close';
import Dialog from '@mui/material/Dialog';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import DialogActions from '@mui/material/DialogActions';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import { Box, Popover } from '@mui/material';
import { showNotification } from 'store/notification.slice';
import {
  GridActionsCellItem,
  GridRowModes,
  type GridColDef,
  type GridRowId,
  type GridRowModel,
  type GridRowModesModel,
} from '@mui/x-data-grid';
import { getDynamicAssetTireInfo } from 'views/TirePressure/util';
import CustomStyledDataGrid from 'components/MuiDataGrid/CustomStyledDataGrid';
// using clientThemePalette like AxleWise modal (no ThemeProvider)
import { CLIENT_THEME, clientThemePalette } from 'utils/clientTheme';
import { useCallback, useEffect, useState } from 'react';
import { useAppDispatch } from 'store/hook';
import type { Asset } from 'types/asset.types';
import useTimeDisplayMode from 'hooks/useTimeDisplayMode';
import { convertUtcToDisplayTime } from 'utils/helpers/dateTime';
import CustomDatePicker from 'components/DatePIcker/CustomDatePicker';
import type { MakeModelDetails } from 'types/make-model.types';
import type {
  TirePattern,
  TirePatternPosition,
} from 'types/tire-pattern.types';
import { useTranslation } from 'react-i18next';

export const FitmentDate = ({
  title,
  open,
  handleAbort,
  enableDarkTheme,
  selectedAsset,
  tireData,
  updateFitmentData,
  makeModelDetails,
  tirePatterns,
  customerId,
}: {
  title: string;
  open: boolean;
  handleAbort: any;
  enableDarkTheme: boolean;
  selectedAsset: Asset;
  tireData: any;
  updateFitmentData: (arg: any, tireCode: string) => Promise<void>;
  makeModelDetails: MakeModelDetails[];
  tirePatterns: {
    tirePatterns: TirePattern[];
    tirePatternPositions: TirePatternPosition[];
  };
  customerId: string;
}) => {
  const { t } = useTranslation();
  const { preferredTimeDisplayMode, timezone } = useTimeDisplayMode();
  const dispatch = useAppDispatch();

  const [rowData, setRowData] = useState<any[]>([]);
  const [rowModesModel, setRowModesModel] = useState<GridRowModesModel>({});
  const [confirmationDialog, setConfirmationDialog] = useState<{
    open: boolean;
    rowId: GridRowId | null;
    newRowData: GridRowModel | null;
    oldRowData: GridRowModel | null;
  }>({
    open: false,
    rowId: null,
    newRowData: null,
    oldRowData: null,
  });

  const handleEditClick = (id: GridRowId) => () => {
    setRowModesModel({ ...rowModesModel, [id]: { mode: GridRowModes.Edit } });
  };

  const handleSaveClick = (id: GridRowId) => () => {
    setRowModesModel({ ...rowModesModel, [id]: { mode: GridRowModes.View } });
  };

  const handleCancelClick = (id: GridRowId) => () => {
    setRowModesModel({
      ...rowModesModel,
      [id]: { mode: GridRowModes.View, ignoreModifications: true },
    });
  };

  const handleConfirmSave = async () => {
    if (!confirmationDialog.newRowData || !confirmationDialog.oldRowData)
      return;

    try {
      const newRow = confirmationDialog.newRowData;
      const datePart = dayjs(newRow.date).format('YYYY-MM-DD');
      const fullString = `${datePart} 00:00:00`;
      const result = dayjs.tz(fullString, 'YYYY-MM-DD HH:mm:ss', timezone);
      const utcISOString = result.toISOString();

      const payload = {
        installedAt: utcISOString,
        changeType: 'tire-added',
        serialNumber: newRow.serialNumber || null,
      };
      const tireCode = newRow.id;
      await updateFitmentData(payload, tireCode);
      dispatch(
        showNotification({
          message: t('fitment_details_updated'),
          type: 'success',
        })
      );

      setRowData((prevData) =>
        prevData.map((row) => (row.id === newRow.id ? { ...newRow } : row))
      );
    } catch (e) {
      dispatch(
        showNotification({
          message: t('error_while_updating_fitment_date'),
          type: 'error',
        })
      );
      setRowModesModel((prev) => ({
        ...prev,
        [confirmationDialog?.oldRowData?.id]: { mode: GridRowModes.Edit },
      }));
    }

    setConfirmationDialog({
      open: false,
      rowId: null,
      newRowData: null,
      oldRowData: null,
    });
  };

  const handleCancelConfirmation = () => {
    if (confirmationDialog.oldRowData) {
      setRowModesModel((prev) => ({
        ...prev,
        [confirmationDialog?.oldRowData?.id]: { mode: GridRowModes.Edit },
      }));
    }
    setConfirmationDialog({
      open: false,
      rowId: null,
      newRowData: null,
      oldRowData: null,
    });
  };

  const processRowUpdate = useCallback(
    async (newRow: GridRowModel, oldRow: GridRowModel) => {
      setConfirmationDialog({
        open: true,
        rowId: newRow.id,
        newRowData: newRow,
        oldRowData: oldRow,
      });
      return oldRow;
    },
    [rowModesModel, setRowModesModel]
  );
  const columnDefs: GridColDef[] = [
    {
      headerName: t('tire_position'),
      field: 'tire',
      editable: false,
      flex: 1,
      headerAlign: 'left',
      align: 'left',
      width: 600,
    },
    {
      field: 'date',
      headerName: 'Fitment Date',
      width: 400,
      editable: false,
      sortable: true,
      filterable: true,
      type: 'date',
      renderCell: (params) => (
        <CalendarCell
          value={params.value}
          row={params.row}
          field={params.field}
          api={params.api}
          rowModesModel={rowModesModel}
        />
      ),

      valueFormatter: (params) => {
        if (!params.value) return '';
        return new Date(params.value).toLocaleDateString();
      },

      sortComparator: (v1, v2) => {
        const date1: any = new Date(v1 || 0);
        const date2: any = new Date(v2 || 0);
        return date1 - date2;
      },
    },
    {
      headerName: 'Serial Number',
      field: 'serialNumber',
      editable: false,
      width: 200,
      headerAlign: 'left',
      align: 'left',
      renderCell: (params) => (
        <SerialNumberCell
          value={params.value}
          row={params.row}
          field={params.field}
          api={params.api}
          rowModesModel={rowModesModel}
        />
      ),
      valueFormatter: (params) => {
        return params.value || 'N/A';
      },
    },
    {
      field: 'actions',
      type: 'actions',
      headerName: '',
      width: 100,
      headerAlign: 'center',
      align: 'center',
      cellClassName: 'actions',
      getActions: ({ id }) => {
        const isInEditMode = rowModesModel[id]?.mode === GridRowModes.Edit;

        if (isInEditMode) {
          return [
            <GridActionsCellItem
              icon={<Save />}
              label={t('save')}
              color="success"
              key={`0-save-button`}
              onClick={handleSaveClick(id)}
            />,
            <GridActionsCellItem
              icon={<Cancel />}
              label={t('cancel')}
              className="textPrimary"
              onClick={handleCancelClick(id)}
              key={`0-cancel-button`}
              color="error"
            />,
          ];
        }

        return [
          <GridActionsCellItem
            icon={<Edit />}
            label={t('edit')}
            className="textPrimary"
            onClick={handleEditClick(id)}
            color="inherit"
            key={`0-edit-button`}
          />,
        ];
      },
    },
  ];

  const palette =
    clientThemePalette[CLIENT_THEME][enableDarkTheme ? 'dark' : 'light'];
  const modalBgColor = palette.boxBackground;
  const modalTextColor = palette.textPrimary;
  const modalTitleBgColor = enableDarkTheme
    ? palette.toolBarBackground
    : palette.topBar;
  const modalBorderColor = palette.borderGrey || palette.border;

  useEffect(() => {
    if (selectedAsset?.device?.deviceId) {
      const assetTireData = tireData[selectedAsset?.device?.deviceId];
      const fitmentData = assetTireData.tireFitmentData ?? [];

      const tireCodes: string[] = assetTireData.tireFitmentData.map(
        (item: any) => item.tireCode
      );
      const assetTireDetails = getDynamicAssetTireInfo(
        makeModelDetails,
        tirePatterns.tirePatternPositions,
        selectedAsset.make,
        selectedAsset.model,
        customerId
      );
      const assetTireNames = assetTireDetails.tireCodes;
      const displayData = tireCodes.map((tireCode) => {
        const tireFitment = fitmentData.find(
          (item: any) => item.tireCode === tireCode
        );
        return {
          id: tireCode,
          tire: assetTireNames[tireCode] ?? tireCode,
          serialNumber: tireFitment?.serialNumber || null,
          date: tireFitment?.installedAt
            ? convertUtcToDisplayTime(
                tireFitment?.installedAt,
                preferredTimeDisplayMode,
                timezone,
                'DD MMM YYYY hh:mm A'
              )
            : null,

          actions: '',
        };
      });

      setRowData(displayData);
    }
  }, [selectedAsset]);

  return (
    <>
      <Dialog
        open={open}
        onClose={handleAbort}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            backgroundColor: modalBgColor,
            color: modalTextColor,
            borderRadius: '12px',
            border: `1px solid ${modalBorderColor}`,
          },
        }}
      >
        <DialogTitle
          sx={{
            backgroundColor: modalTitleBgColor,
            borderBottom: `1px solid ${modalBorderColor}`,
            padding: '12px 24px',
            fontSize: '1.25rem',
            fontWeight: 500,
          }}
        >
          {title}
          <IconButton
            aria-label="close"
            onClick={handleAbort}
            sx={{
              position: 'absolute',
              right: 12,
              top: 12,
              color: (theme) => theme.palette.grey[500],
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ padding: '0px', minHeight: '400px' }}>
          <div
            style={{
              height: 'calc(100vh - 330px)',
              maxHeight: '400px',
              width: '100%',
            }}
          >
            <CustomStyledDataGrid
              columns={columnDefs}
              rows={rowData}
              disableColumnMenu={true}
              pagination
              rowModesModel={rowModesModel}
              editMode="row"
              disableRowSelectionOnClick
              rowHeight={45}
              columnHeaderHeight={30}
              experimentalFeatures={{ columnGrouping: true }}
              showColumnVerticalBorder
              processRowUpdate={processRowUpdate}
              sx={{
                // Root + scroller backgrounds
                backgroundColor: modalBgColor,
                '&.MuiDataGrid-root': {
                  backgroundColor: modalBgColor,
                },
                '& .MuiDataGrid-virtualScroller': {
                  backgroundColor: modalBgColor,
                },
                '& .MuiDataGrid-virtualScrollerContent': {
                  backgroundColor: modalBgColor,
                },
                '& .MuiDataGrid-columnHeaders': {
                  backgroundColor: modalTitleBgColor,
                  color: modalTextColor,
                  borderBottom: `1px solid ${modalBorderColor}`,
                },
                '& .MuiDataGrid-columnSeparator': {
                  color: modalBorderColor,
                },
                '& .MuiDataGrid-cell': {
                  color: modalTextColor,
                  borderBottom: `1px solid ${modalBorderColor}`,
                },
                '& .MuiDataGrid-row': {
                  backgroundColor: modalBgColor,
                  // Yokohama: force even rows neutral (override bluish default)
                  '&:nth-of-type(even)': {
                    backgroundColor: modalBgColor,
                  },
                  '&:nth-of-type(odd)': {
                    backgroundColor: enableDarkTheme
                      ? 'rgba(255,255,255,0.02)'
                      : 'rgba(0,0,0,0.02)',
                  },
                  '&:hover': {
                    backgroundColor: enableDarkTheme
                      ? 'rgba(255,255,255,0.05)'
                      : 'rgba(0,0,0,0.04)',
                  },
                },
              }}
            />
          </div>
        </DialogContent>
      </Dialog>

      <Dialog
        open={confirmationDialog.open}
        onClose={handleCancelConfirmation}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            backgroundColor: modalBgColor,
            color: modalTextColor,
            borderRadius: '12px',
            border: `1px solid ${modalBorderColor}`,
          },
        }}
      >
        <DialogTitle
          sx={{
            backgroundColor: modalTitleBgColor,
            borderBottom: `1px solid ${modalBorderColor}`,
            padding: '12px 24px',
            fontSize: '1.25rem',
            fontWeight: 500,
          }}
        >
          {t('confirm_fitment_details_change')}
        </DialogTitle>
        <DialogContent sx={{ padding: '24px' }}>
          <Box sx={{ py: 2 }}>
            {t('are_you_sure_you_want_to_update_the_fitment_details_for')}{' '}
            <strong>{confirmationDialog.newRowData?.tire}</strong>
            ?
            <br />
            <br />
            {confirmationDialog.oldRowData?.serialNumber !==
              confirmationDialog.newRowData?.serialNumber && (
              <>
                {t('serial_number')}{' '}
                <strong>
                  {confirmationDialog.oldRowData?.serialNumber || 'N/A'}{' '}
                  {t('x2192')}{' '}
                  {confirmationDialog.newRowData?.serialNumber || 'N/A'}
                </strong>
                <br />
                <br />
              </>
            )}
            {confirmationDialog.oldRowData?.date !==
              confirmationDialog.newRowData?.date && (
              <>
                {t('fitment_date')}{' '}
                <strong>
                  {confirmationDialog.oldRowData?.date
                    ? dayjs(confirmationDialog.oldRowData.date).format(
                        'YYYY MMMM DD'
                      )
                    : 'Not set'}{' '}
                  {t('x2192')}{' '}
                  {confirmationDialog.newRowData?.date
                    ? dayjs(confirmationDialog.newRowData.date).format(
                        'YYYY MMMM DD'
                      )
                    : 'Not set'}
                </strong>
                <br />
                <br />
              </>
            )}
            {confirmationDialog.oldRowData?.serialNumber ===
              confirmationDialog.newRowData?.serialNumber &&
              confirmationDialog.oldRowData?.date ===
                confirmationDialog.newRowData?.date && (
                <>
                  {t('new_serial_number')}{' '}
                  <strong>
                    {confirmationDialog.newRowData?.serialNumber || 'N/A'}
                  </strong>
                  <br />
                  <br />
                  {t('new_fitment_date')}{' '}
                  <strong>
                    {confirmationDialog.newRowData?.date
                      ? dayjs(confirmationDialog.newRowData.date).format(
                          'YYYY MMMM DD'
                        )
                      : 'Not set'}
                  </strong>
                </>
              )}
          </Box>
        </DialogContent>
        <DialogActions sx={{ padding: '12px 24px', gap: 1 }}>
          <Button
            onClick={handleCancelConfirmation}
            variant="outlined"
            color="error"
            sx={{ minWidth: '80px' }}
          >
            {t('cancel')}
          </Button>
          <Button
            onClick={handleConfirmSave}
            variant="contained"
            color="success"
            sx={{ minWidth: '80px' }}
          >
            {t('confirm')}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

const CalendarCell = ({ value, row, field, api, rowModesModel }: any) => {
  const [selectedDate, setSelectedDate] = useState(value ? dayjs(value) : null);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

  const isInEditMode = rowModesModel?.[row.id]?.mode === GridRowModes.Edit;

  const handleDateChange = (newDate: any) => {
    setSelectedDate(newDate);
    setAnchorEl(null);
    api.updateRows([{ id: row.id, [field]: newDate?.toISOString() }]);
  };

  const handleCalendarClick = (event: React.MouseEvent<HTMLElement>) => {
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const open = Boolean(anchorEl);

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        height: '100%',
        gap: 1,
        width: '100%',
      }}
    >
      <span>{selectedDate ? selectedDate.format('YYYY MMMM DD') : ''}</span>

      {isInEditMode && (
        <>
          <IconButton
            size="small"
            onClick={handleCalendarClick}
            sx={{ padding: '4px', marginLeft: 'auto' }}
          >
            <CalendarToday fontSize="small" />
          </IconButton>

          <Popover
            open={open}
            anchorEl={anchorEl}
            onClose={handleClose}
            anchorOrigin={{
              vertical: 'center',
              horizontal: 'left',
            }}
            transformOrigin={{
              vertical: 'center',
              horizontal: 'right',
            }}
            disableRestoreFocus
          >
            <Box sx={{ p: 1 }}>
              <CustomDatePicker
                value={selectedDate}
                onChange={handleDateChange}
                maxDate={dayjs()}
              />
            </Box>
          </Popover>
        </>
      )}
    </Box>
  );
};

const SerialNumberCell = ({ value, row, field, api, rowModesModel }: any) => {
  const { t } = useTranslation();
  const [serialNumber, setSerialNumber] = useState(value || '');

  const isInEditMode = rowModesModel?.[row.id]?.mode === GridRowModes.Edit;

  const handleSerialNumberChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const newValue = event.target.value;
    setSerialNumber(newValue);
    api.updateRows([{ id: row.id, [field]: newValue }]);
  };

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        height: '100%',
        width: '100%',
        padding: '0 8px',
      }}
    >
      {isInEditMode ? (
        <input
          type="text"
          value={serialNumber}
          onChange={handleSerialNumberChange}
          placeholder={t('enter_serial_number')}
          style={{
            width: '100%',
            padding: '4px 8px',
            border: '1px solid #ccc',
            borderRadius: '4px',
            fontSize: '14px',
            backgroundColor: 'white',
            outline: 'none',
          }}
          autoFocus
        />
      ) : (
        <span
          style={{
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            maxWidth: '100%',
            display: 'inline-block',
          }}
        >
          {serialNumber || 'N/A'}
        </span>
      )}
    </Box>
  );
};
